/**
 * The sample rate a file declares in its own header.
 *
 * Web Audio decodes every file at the AudioContext's rate (often 48 kHz), so
 * `AudioBuffer.sampleRate` says nothing about the upload. This reads the rate
 * the file was made at for WAV, MP3 and Ogg Vorbis, and returns null for any
 * other container so the caller can fall back to the decoded rate.
 */

const MPEG_RATES: Record<number, readonly number[]> = {
  3: [44100, 48000, 32000], // MPEG 1
  2: [22050, 24000, 16000], // MPEG 2
  0: [11025, 12000, 8000], // MPEG 2.5
}

function ascii(bytes: Uint8Array, offset: number, length: number): string {
  let out = ''
  for (let i = offset; i < offset + length && i < bytes.length; i++) out += String.fromCharCode(bytes[i])
  return out
}

function wavRate(bytes: Uint8Array): number | null {
  if (ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 4) !== 'WAVE') return null
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  let offset = 12
  while (offset + 8 <= bytes.length) {
    const size = view.getUint32(offset + 4, true)
    if (ascii(bytes, offset, 4) === 'fmt ') {
      return offset + 16 <= bytes.length ? view.getUint32(offset + 12, true) : null
    }
    offset += 8 + size + (size % 2)
  }
  return null
}

function mp3Rate(bytes: Uint8Array): number | null {
  let start = 0
  const tagged = ascii(bytes, 0, 3) === 'ID3' && bytes.length >= 10
  if (tagged) {
    const size = ((bytes[6] & 0x7f) << 21) | ((bytes[7] & 0x7f) << 14) | ((bytes[8] & 0x7f) << 7) | (bytes[9] & 0x7f)
    start = 10 + size + (bytes[5] & 0x10 ? 10 : 0)
  }
  // Untagged: the first frame must start the file, or an M4A or WebM could match by chance.
  for (let i = start; i + 3 < bytes.length && i < start + (tagged ? 8192 : 1); i++) {
    if (bytes[i] !== 0xff || (bytes[i + 1] & 0xe0) !== 0xe0) continue
    const version = (bytes[i + 1] >> 3) & 3
    const layer = (bytes[i + 1] >> 1) & 3
    const rateIndex = (bytes[i + 2] >> 2) & 3
    if (version === 1 || layer === 0 || rateIndex === 3) continue
    return MPEG_RATES[version][rateIndex]
  }
  return null
}

function vorbisRate(bytes: Uint8Array): number | null {
  if (ascii(bytes, 0, 4) !== 'OggS' || bytes.length < 28) return null
  const packet = 27 + bytes[26]
  if (bytes[packet] !== 0x01 || ascii(bytes, packet + 1, 6) !== 'vorbis') return null
  if (packet + 16 > bytes.length) return null
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(packet + 12, true)
}

export function readDeclaredSampleRate(bytes: Uint8Array): number | null {
  const rate = wavRate(bytes) ?? vorbisRate(bytes) ?? mp3Rate(bytes)
  return rate !== null && rate > 0 ? rate : null
}
